import { data, Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { getMdxPage, getRelatedPosts } from '#app/utils/mdx.server.ts'
import {
	MdxContent,
	getBlogPostMeta,
	getBlogPostStructuredData,
} from '#app/utils/mdx.tsx'
import { type Route } from './+types/$slug.ts'

export const meta: Route.MetaFunction = ({ data: loaderData }) => {
	if (!loaderData?.post) {
		return [
			{ title: 'Post Not Found | MyBillingsMethod Blog' },
			{ name: 'description', content: 'This blog post could not be found.' },
		]
	}

	return getBlogPostMeta(loaderData.post, loaderData.siteUrl)
}

export async function loader({ request, params }: Route.LoaderArgs) {
	const { slug } = params
	if (!slug) {
		throw data({ message: 'Slug is required' }, { status: 400 })
	}

	const post = await getMdxPage('blog', slug)
	if (!post) {
		throw data({ message: 'Post not found' }, { status: 404 })
	}

	const relatedPosts = await getRelatedPosts(
		slug,
		post.frontmatter.categories,
		3,
	)
	const siteUrl = getDomainUrl(request)

	return { post, relatedPosts, siteUrl }
}

export default function BlogPost({ loaderData }: Route.ComponentProps) {
	const { post, relatedPosts, siteUrl } = loaderData
	const structuredData = getBlogPostStructuredData(post, siteUrl)

	return (
		<>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
			/>

			<div className="min-h-screen bg-stone-50">
				<Navigation />

				<article className="mx-auto max-w-2xl px-6 py-16">
					{/* Back link */}
					<Link
						to="/blog"
						className="mb-8 inline-flex items-center gap-2 text-sm text-stone-500 transition-colors hover:text-stone-900"
						prefetch="intent"
					>
						<Icon name="arrow-left" className="h-4 w-4" />
						Back to journal
					</Link>

					{/* Header */}
					<header className="mb-12">
						{/* Date and reading time */}
						<div className="mb-4 flex items-center gap-3 text-sm text-stone-500">
							<time dateTime={post.frontmatter.date}>{post.dateDisplay}</time>
							<span className="text-stone-300">·</span>
							<span>{post.readTime.text}</span>
						</div>

						{/* Title */}
						<h1 className="mb-6 font-serif text-3xl font-medium leading-tight text-stone-900 md:text-4xl lg:text-5xl">
							{post.frontmatter.title}
						</h1>

						{/* Description */}
						<p className="text-lg leading-relaxed text-stone-600">
							{post.frontmatter.description}
						</p>

						{/* Categories */}
						{post.frontmatter.categories.length > 0 && (
							<div className="mt-6 flex flex-wrap gap-2">
								{post.frontmatter.categories.map((category) => (
									<span
										key={category}
										className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600"
									>
										{category}
									</span>
								))}
							</div>
						)}
					</header>

					{/* Featured image */}
					{post.frontmatter.image && (
						<figure className="mb-12">
							<img
								src={post.frontmatter.image}
								alt={post.frontmatter.imageAlt ?? post.frontmatter.title}
								className="w-full rounded-lg"
							/>
						</figure>
					)}

					{/* Content */}
					<div className="prose-journal">
						<MdxContent code={post.code} />
					</div>

					{/* Author */}
					<footer className="mt-16 border-t border-stone-200 pt-8">
						<div className="flex items-center gap-4">
							<div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
								<Icon name="lock-closed" className="h-6 w-6 text-purple-600" />
							</div>
							<div>
								<p className="font-medium text-stone-900">
									{post.frontmatter.author}
								</p>
								<p className="text-sm text-stone-500">
									Privacy-first fertility tracking
								</p>
							</div>
						</div>
					</footer>
				</article>

				{/* Related posts */}
				{relatedPosts.length > 0 && (
					<section className="border-t border-stone-200 bg-white py-16">
						<div className="mx-auto max-w-2xl px-6">
							<h2 className="mb-8 font-serif text-2xl font-medium text-stone-900">
								Related articles
							</h2>
							<div className="space-y-8">
								{relatedPosts.map((relatedPost) => (
									<Link
										key={relatedPost.slug}
										to={`/blog/${relatedPost.slug}`}
										className="group block"
										prefetch="intent"
									>
										<div className="mb-2 text-sm text-stone-500">
											{relatedPost.dateDisplay}
										</div>
										<h3 className="font-medium text-stone-900 transition-colors group-hover:text-purple-700">
											{relatedPost.frontmatter.title}
										</h3>
										<p className="mt-1 text-sm text-stone-600">
											{relatedPost.frontmatter.description}
										</p>
									</Link>
								))}
							</div>
						</div>
					</section>
				)}

				<PageFooter />
			</div>
		</>
	)
}

function Navigation() {
	return (
		<nav className="border-b border-stone-200 bg-stone-50/80 backdrop-blur-sm">
			<div className="mx-auto max-w-2xl px-6 py-4">
				<div className="flex items-center justify-between">
					<Link
						to="/"
						className="flex items-center space-x-2 text-stone-900 transition-colors hover:text-purple-700"
					>
						<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
						<span className="font-medium">MyBillingsMethod</span>
					</Link>
					<Link
						to="/blog"
						className="text-sm text-stone-600 transition-colors hover:text-stone-900"
					>
						All articles
					</Link>
				</div>
			</div>
		</nav>
	)
}

function PageFooter() {
	return (
		<footer className="border-t border-stone-200 bg-stone-50 py-12">
			<div className="mx-auto max-w-2xl px-6">
				<div className="flex flex-col items-center justify-between gap-4 text-sm text-stone-500 sm:flex-row">
					<p>&copy; {new Date().getFullYear()} MyBillingsMethod</p>
					<div className="flex gap-6">
						<Link to="/privacy" className="hover:text-stone-900">
							Privacy
						</Link>
						<Link to="/tos" className="hover:text-stone-900">
							Terms
						</Link>
						<Link to="/support" className="hover:text-stone-900">
							Support
						</Link>
					</div>
				</div>
			</div>
		</footer>
	)
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	return (
		<div className="min-h-screen bg-stone-50">
			<Navigation />
			<div className="mx-auto max-w-2xl px-6 py-32 text-center">
				<div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-stone-100">
					<Icon name="file-text" className="h-10 w-10 text-stone-400" />
				</div>
				<h1 className="mb-4 font-serif text-3xl font-medium text-stone-900">
					Article not found
				</h1>
				<p className="mb-8 text-stone-600">
					Sorry, we couldn't find the article you're looking for.
				</p>
				<Link
					to="/blog"
					className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-purple-700"
				>
					<Icon name="arrow-left" className="h-4 w-4" />
					Back to journal
				</Link>
			</div>
		</div>
	)
}
